import api from '@/services/api'

export interface ChatMessage {
  id: number
  role: 'user' | 'ai' | 'agent'
  content: string
  // 'text' (default) | 'status' (transient flow-status line) | 'confirmation' (preview card)
  contentType: string
  senderName: string
  metadata?: Record<string, unknown>
  createdAt: string
  actions?: ClientAction[]
  // Preview-card payload, present only when contentType === 'confirmation'
  confirmation?: ConfirmationPreview
}

export interface ConfirmationPreview {
  intent: string
  sectionId?: string
  selectionToken?: number
  preview?: Record<string, unknown>
  // Frontend-only UI state once the user picks Accept / Reject / Modify
  resolved?: boolean
  resolvedLabel?: string
}

export interface ClientAction {
  actionId?: string
  type: string
  route?: string
  command?: string
  params?: Record<string, unknown>
  // redirect: optional follow-up chat line shown after navigation
  prompt?: string
  // request_confirmation: preview-card payload
  intent?: string
  sectionId?: string
  selectionToken?: number
  preview?: Record<string, unknown>
}

export interface ChatContext {
  route?: string
  workspaceId?: string
  pageId?: string
  screenTitle?: string
  systemHint?: string
  locale?: string
  selectedComponent?: Record<string, unknown>
  // Builder selection — id of the component the user has selected on the
  // canvas, plus a monotonic token that bumps every time the selection
  // moves. Both shipped by `useAIContext()` from `editorStore`.
  // `apply_section_update` checks `selectionToken` against the live
  // editor token and refuses stale applies (see aiActionDispatcher.ts).
  selectedSectionId?: string
  selectionToken?: number
  // Price/quality choice for this request (see backend services/chat/chatModels.js).
  modelTier?: ModelTier
}

export type ModelTier = 'economy' | 'balanced' | 'quality' | 'chatgpt'

// A page being built in the background by the chat (build_page). Lives on the
// chat message's metadata and is refreshed live by the SSE `build` event.
export interface PageBuildState {
  status: 'running' | 'done' | 'partial' | 'failed' | 'interrupted' | 'cancelled'
  step?: 'context' | 'design' | 'page' | 'sections' | 'review' | 'done'
  progress?: number
  pageName?: string
  pageId?: number | null
  editorRoute?: string | null
  bands?: { id: string; label: string; status: 'pending' | 'saved' | 'failed' }[]
  warnings?: string[]
  error?: string | null
  tier?: ModelTier
}

export interface ConversationSummary {
  id: number
  token: string
  status: string
  lastMessageAt: string
  subject: string | null
  // What kind of request the chat was — drives the icon in the history list.
  kind: 'support' | 'knowledge' | 'action' | 'general' | null
  lastMessage: string | null
}

const BASE = '/private/chat'

export const chatService = {
  async createConversation(message: string, context: ChatContext) {
    return api.post<{ conversationId: number; conversationToken: string; message: ChatMessage }>(
      `${BASE}/conversations`,
      { content: message, context }
    )
  },

  async sendMessage(conversationId: number, content: string, context: ChatContext) {
    return api.post<{ message: ChatMessage }>(
      `${BASE}/conversations/${conversationId}/messages`,
      { content, context }
    )
  },

  // Confirm to the server whether a builder command applied. Drives the
  // charge-and-refund reconciliation for merchant-chat modifications.
  async builderAck(conversationId: number, actionId: string, ok: boolean, reason?: string) {
    return api.post<{ refunded: boolean }>(
      `${BASE}/conversations/${conversationId}/builder-ack`,
      { actionId, ok, reason }
    )
  },

  async resumePageBuild(conversationId: number, messageId: number, modelTier?: ModelTier) {
    return api.post<{ buildId: number; status: string }>(
      `${BASE}/conversations/${conversationId}/page-builds/${messageId}/resume`,
      { modelTier }
    )
  },

  async cancelPageBuild(conversationId: number, messageId: number) {
    return api.post<{ buildId: number; status: string }>(
      `${BASE}/conversations/${conversationId}/page-builds/${messageId}/cancel`
    )
  },

  async getMessages(conversationId: number) {
    return api.get<{ messages: ChatMessage[]; status?: string }>(
      `${BASE}/conversations/${conversationId}/messages`
    )
  },

  async listConversations() {
    return api.get<{ conversations: ConversationSummary[] }>(
      `${BASE}/conversations`
    )
  },

  createStream(conversationId: number): EventSource {
    // Same fallback the other services use — localhost in dev when VITE_API_URL
    // is unset. (Was a hardcoded staging host, which broke local dev and would
    // point production at staging if the env var ever went missing.)
    const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:3000'
    const url = `${apiUrl}${BASE}/conversations/${conversationId}/stream`
    return new EventSource(url, { withCredentials: true })
  }
}
